/**
 * Bridges a Mango Personal (Social Circle) session into a business-scoped Customer session, via
 * POST /customer-auth/session-from-personal (backend/src/routes/customer-auth.routes.ts) — that
 * endpoint finds-or-links a Customer row for (businessId, this PersonalAccount) and mints a
 * Customer-type JWT. Needed because event-chat (ticket holder chat) only accepts Customer auth.
 */
import { customerBridgeApi } from '../services/api';

function storageKey(businessId: string): string {
  return `mangoCustomerToken:${businessId}`;
}

function decodeJwtExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isUsable(token: string | null): token is string {
  if (!token) return false;
  const expiry = decodeJwtExpiryMs(token);
  // 60s buffer so a token doesn't expire mid-request
  return expiry === null || expiry > Date.now() + 60_000;
}

export async function getCustomerToken(businessId: string): Promise<string> {
  const cached = localStorage.getItem(storageKey(businessId));
  if (isUsable(cached)) return cached;

  const res = await customerBridgeApi.sessionFromPersonal(businessId);
  if (!res?.success || !res?.token) {
    throw new Error(res?.error || 'Failed to link your account for this event\'s chat');
  }
  localStorage.setItem(storageKey(businessId), res.token);
  return res.token as string;
}
